import { useState, useEffect, lazy, Suspense } from "react";
import { supabase } from "./supabase";
import { DEV_MODE, API_URL, ADMIN_EMAIL } from "./config/env";
import { styles } from "./styles/appStyles";
import { resetAnalytics } from "./lib/analytics";
import TermsPage from "./components/TermsPage";
import PrivacyPage from "./components/PrivacyPage";
import SuccessPage from "./components/SuccessPage";
import ResetPasswordPage from "./components/ResetPasswordPage";
import HamburgerMenu from "./components/HamburgerMenu";
import Paywall from "./components/Paywall";
import AuthScreen from "./components/AuthScreen";
import LandingPage from "./components/LandingPage";
import PitcherlyApp from "./components/PitcherlyApp";

// AdminPage is admin-only (the /admin route, gated to ADMIN_EMAIL) — lazy-loaded so it ships as a
// separate chunk and stays out of every normal/paying user's initial bundle.
const AdminPage = lazy(() => import("./components/AdminPage"));

export default function App() {
  const [session, setSession] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentMsg, setPaymentMsg] = useState("Setting up your account...");
  const [showAuth, setShowAuth] = useState(null); // null | "login" | "signup"
  const [demoMode, setDemoMode] = useState(false);

  const isAccessRowValid = (row) => {
    if (!row) return false;
    if (row.status !== "active" && row.status !== "trialing") return false;
    if (row.access_expires_at) {
      const exp = new Date(row.access_expires_at);
      if (!isNaN(exp.getTime()) && exp.getTime() < Date.now()) return false;
    }
    return true;
  };

  const checkSubscription = async (user) => {
    try {
      // Try by user_id first
      const { data: row, error } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (row && !error && isAccessRowValid(row)) {
        const sub = { active: true, plan: row.plan, stripe_status: row.status, ...row };
        setSubscription(sub);
        return true;
      }

      // Fallback: try by email
      if (user.email) {
        const { data: emailRow, error: emailErr } = await supabase
          .from('subscriptions')
          .select('*')
          .eq('email', user.email)
          .single();

        if (emailRow && !emailErr && isAccessRowValid(emailRow)) {
          const sub = { active: true, plan: emailRow.plan, stripe_status: emailRow.status, ...emailRow };
          setSubscription(sub);
          return true;
        }
      }

      setSubscription(null);
      return false;
    } catch {
      setSubscription(null);
      return false;
    }
  };

  const checkSubscriptionWithRetry = async (user, retries = 10) => {
    for (let i = 0; i < retries; i++) {
      const found = await checkSubscription(user);
      if (found) return true;
      if (i < retries - 1) {
        await new Promise(resolve => setTimeout(resolve, 2000));
      }
    }
    return false;
  };

  useEffect(() => {
    if (DEV_MODE || !supabase) { setAuthLoading(false); return; }

    const urlParams = new URLSearchParams(window.location.search);
    const paymentStatus = urlParams.get("payment");
    const sessionId = urlParams.get("session_id");
    const isPaymentRedirect = paymentStatus === "success" && sessionId;

    // Check for existing session
    supabase.auth.getSession().then(async ({ data: { session: s } }) => {
      setSession(s);
      if (s) {
        if (isPaymentRedirect) {
          // Payment redirect — sync from Stripe directly, then retry subscription check
          setPaymentLoading(true);
          setPaymentMsg("Setting up your account...");
          window.history.replaceState({}, document.title, "/");
          try {
            await fetch(`${API_URL}/verify-session?session_id=${sessionId}`);
          } catch (e) { console.warn("verify-session failed:", e); }
          // Immediately call /sync-subscription as a bulletproof fallback
          let synced = false;
          for (let i = 0; i < 5; i++) {
            try {
              const r = await fetch(`${API_URL}/sync-subscription`, {
                method: "POST",
                headers: { "Content-Type": "application/json", ...(s?.access_token ? { Authorization: `Bearer ${s.access_token}` } : {}) },
                body: JSON.stringify({ email: s.user.email, user_id: s.user.id }),
              });
              const d = await r.json();
              if (d.synced) { synced = true; break; }
            } catch (e) { console.warn("sync-subscription attempt failed:", e); }
            if (i === 1) setPaymentMsg("Almost ready...");
            await new Promise(resolve => setTimeout(resolve, 2000));
          }
          const found = await checkSubscriptionWithRetry(s.user);
          if (!found && !synced) {
            setPaymentMsg("Your payment was received! If you don't see your account updated within a minute please refresh the page or contact support@pitcherly.com");
            await new Promise(resolve => setTimeout(resolve, 4000));
          }
          setPaymentLoading(false);
        } else {
          await checkSubscription(s.user);
        }
      }
      setAuthLoading(false);
    });

    // Listen for auth changes
    const { data: { subscription: authSub } } = supabase.auth.onAuthStateChange((_event, s) => {
      setSession(s);
      if (s && !paymentLoading) checkSubscription(s.user);
      else if (!s) { setSubscription(null); setAuthLoading(false); }
    });

    return () => authSub.unsubscribe();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLogout = async () => {
    if (supabase) await supabase.auth.signOut();
    resetAnalytics();
    setSession(null);
    setSubscription(null);
  };

  const path = window.location.pathname;
  if (path === "/reset-password") return <ResetPasswordPage />;
  if (path === "/terms") return <TermsPage />;
  if (path === "/privacy") return <PrivacyPage />;
  if (path === "/success") return <SuccessPage />;

  // Admin route — only accessible to admin email
  if (path === "/admin") {
    if (authLoading) return <><style>{styles}</style><div className="success-page"><div className="success-icon">...</div><div className="success-title">Loading...</div></div></>;
    if (!session) return <AuthScreen onAuth={(s) => setSession(s)} />;
    if (session.user.email !== ADMIN_EMAIL) return <><style>{styles}</style><div className="success-page"><div className="success-title">Access Denied</div></div></>;
    return (
      <Suspense fallback={<><style>{styles}</style><div className="success-page"><div className="spinner" style={{ width: 32, height: 32, borderWidth: 3 }} /></div></>}>
        <AdminPage userEmail={session.user.email} onLogout={handleLogout} />
      </Suspense>
    );
  }

  // Dev mode bypass. Auth-less (no Supabase session) — and the backend's _resolve_user_id only
  // skips JWT verification for "demo-"prefixed ids, so the dev user MUST be demo-prefixed or every
  // generation 401s. (DEV_MODE is never true in a prod build, so this is prod-safe.)
  if (DEV_MODE) return <PitcherlyApp subscription={{ plan: "pro", active: true }} userId="demo-dev" userEmail="dev@pitcherly.io" onLogout={() => {}} />;

  if (authLoading || paymentLoading) return (
    <>
      <HamburgerMenu />
      <style>{styles}</style>
      <div className="success-page">
        <div style={{fontFamily:"'Fraunces', serif",fontSize:28,fontWeight:500,color:"var(--text)",marginBottom:24}}>
          Pitcher<em style={{color:"var(--accent)",fontStyle:"italic"}}>ly</em>
        </div>
        <div className="spinner" style={{width:32,height:32,borderWidth:3,marginBottom:20}} />
        <div className="success-title" style={{fontSize:16}}>{paymentLoading ? paymentMsg : "Loading..."}</div>
      </div>
    </>
  );

  // Demo mode — render the app as a guest
  if (!session && demoMode) {
    return (
      <PitcherlyApp
        subscription={{ plan: "starter", status: "active", stripe_status: "active", active: true }}
        userId="demo-guest"
        userEmail="demo@pitcherly.com"
        isDemoMode={true}
        onExitDemo={() => { setDemoMode(false); setShowAuth("signup"); }}
        onLogout={() => { setDemoMode(false); setShowAuth(null); }}
      />
    );
  }

  // Not logged in — show landing page or auth screen
  if (!session) {
    if (showAuth) {
      return (
        <>
          <HamburgerMenu />
          <AuthScreen
            onAuth={(s) => { setSession(s); setShowAuth(null); }}
            initialTab={showAuth}
            onBack={() => setShowAuth(null)}
            onTryDemo={() => { setShowAuth(null); setDemoMode(true); }}
          />
        </>
      );
    }
    return (
      <>
        <HamburgerMenu />
        <LandingPage
          onGoToAuth={(mode) => setShowAuth(mode || "login")}
          onTryDemo={() => setDemoMode(true)}
        />
      </>
    );
  }

  // Logged in but no subscription
  if (!subscription) return <><HamburgerMenu /><Paywall userId={session.user.id} userEmail={session.user.email} onLogout={handleLogout} /></>;

  // Fully authenticated with active subscription
  return <PitcherlyApp subscription={subscription} userId={session.user.id} userEmail={session.user.email} onLogout={handleLogout} />;
}

